import { memo, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Box, Dialog, Typography } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import Grid from '@mui/material/Grid'
import { useSnackbar } from 'notistack'

import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { useGetOrderQuery, useSaveOrderProgressMutation, useUpdateOrderMutation } from '@/app/services/orders'
import { selectOpenGuestLoginDialog, setOpenGuestLoginDialog } from '@/app/slices/guestUserSlice'
import { setSelectedOrderPaymentStatus, setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import AppToolbar from '@/common/components/AppBar/AppToolbar'
import GuestLogin from '@/common/components/GuestLogin/GuestLogin'
import { useAssignOrderToUser } from '@/common/hooks/useAssignOrderToUser'
import { useOrderNavigation } from '@/common/hooks/useOrderNavigation'
import { Paths } from '@/common/utils'
import { ORDER_STATUS_ARRAY, ORDER_USER_TYPE } from '@/common/utils/Constants/Constants'

import OrderHeader from '../order/components/OrderHeader/OrderHeader'

import OrderReviewPartsTable from './components/OrderReviewParts/OrderReviewPartsTable'
import OrderReviewSummary from './components/OrderReviewSummary/OrderReviewSummary'

const classes = {
    section: {
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: `1px solid ${blueGrey[200]}`,
        width: '100%',
    },
}

const OrderReview = () => {
    const { t } = useTranslation()
    const { orderId } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const navigation = useOrderNavigation('review')
    const { enqueueSnackbar } = useSnackbar()
    const { isAuthenticated } = useKindeAuth()

    const organisationId = useSelector(selectOrganisationId)
    const openGuestLoginDialog = useSelector(selectOpenGuestLoginDialog)

    const {
        data: order,
        isLoading,
        isSuccess,
    } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    useAssignOrderToUser(order, false)

    const { isFetching: isFetchingItems, isLoading: isLoadingItems } = useGetOrderItemsQuery({
        organisationId,
        orderId,
    })

    const [updateOrder] = useUpdateOrderMutation()
    const [saveProgress] = useSaveOrderProgressMutation({ fixedCacheKey: 'shared-save-order-progress' })

    const isDraft = useMemo(() => {
        return [ORDER_STATUS_ARRAY.Draft].includes(order?.status)
    }, [order?.status])

    const handleGuestDialogClose = () => {
        dispatch(setOpenGuestLoginDialog(null))
    }

    const handleCancelGuestDialogClose = useCallback(() => {
        if (order?.customerContactId) {
            navigate(Paths.HOME_PATHNAME)
        }
    }, [order, navigate])

    const updateOrderCustomer = useCallback(
        async (user) => {
            try {
                await updateOrder({
                    orderId: order.id,
                    organisationId: order.organisationId,
                    order: {
                        name: order.name,
                        quoteId: order.id,
                        customerContactId: user.contactId,
                        customerId: user.customerId,
                        quoteUserType: isAuthenticated ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest,
                    },
                }).unwrap()
            } catch (error) {
                console.error(error)
                enqueueSnackbar(t('Error updating order'), { variant: 'error' })
            }
        },
        [isAuthenticated]
    )

    const saveOrderProgress = async () => {
        try {
            await saveProgress({
                orderId: order.id,
                organisationId: order.organisationId,
            }).unwrap()
            enqueueSnackbar(t('Order progress saved successfully'), { variant: 'success' })
        } catch (error) {
            console.error(error)
            enqueueSnackbar(t('Error saving order progress'), { variant: 'error' })
        }
    }

    const handleSuccessGuestDialogClose = async (guestUser) => {
        try {
            if (!order?.customerContactId && order?.quoteUserType === 'Guest') {
                await updateOrderCustomer(guestUser)
            }

            if (openGuestLoginDialog === 'save-progress') {
                await saveOrderProgress()
            }

            if (openGuestLoginDialog === 'checkout') {
                navigate(Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', orderId))
            }
        } catch (error) {
            console.error(error)
            enqueueSnackbar(t('Error handling guest dialog closing'), { variant: 'error' })
            dispatch(setOpenGuestLoginDialog(null))
        }
    }

    useEffect(() => {
        if (order) {
            dispatch(setSelectedOrderPaymentStatus(order?.paymentStatus))
            dispatch(setSelectedOrderStatus(order?.status))
        }
    }, [order])

    if (navigation) return navigation

    if (!isLoading && isSuccess && isDraft) {
        return (
            <>
                <AppToolbar key={`${order?.id}-toolbar`} />

                {openGuestLoginDialog ? (
                    <Dialog
                        maxWidth="sm"
                        open={Boolean(openGuestLoginDialog)}
                        onClose={handleGuestDialogClose}
                    >
                        <GuestLogin
                            onCancelClose={handleCancelGuestDialogClose}
                            onSuccessClose={handleSuccessGuestDialogClose}
                        />
                    </Dialog>
                ) : (
                    <>
                        <Grid
                            alignContent="flex-start"
                            component="section"
                            size={{
                                lg: 9,
                                xs: 12,
                            }}
                            spacing={3}
                            container
                        >
                            <Grid size={12}>
                                <OrderHeader key={`${order?.id}-header`} />
                            </Grid>

                            {isLoadingItems || isFetchingItems ? (
                                <Grid
                                    size={12}
                                    sx={[classes.section, { minHeight: 'calc(100vh - 348px)' }]}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: 1,
                                        }}
                                    >
                                        <Typography
                                            component="h2"
                                            variant="h6"
                                        >
                                            {t('Loading order items, please wait...')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ) : null}

                            {!isLoadingItems && !isFetchingItems && order?.numberOfItems ? (
                                <Grid
                                    size={12}
                                    sx={classes.section}
                                >
                                    <OrderReviewPartsTable />
                                </Grid>
                            ) : null}
                        </Grid>

                        <Grid
                            component="aside"
                            size={{
                                lg: 3,
                                xs: 12,
                            }}
                            sx={[classes.section, { position: 'sticky', top: '90px' }]}
                        >
                            <OrderReviewSummary key={`${order?.id}-summary`} />
                        </Grid>
                    </>
                )}
            </>
        )
    }
}

export default memo(OrderReview)
